/**
 * Each Sub-Category
 * 
 * - Used by {@link Vehicle_Categories}
 * @typedef {object} Vehicle_Sub_Categories
 * @prop {string} subcategory_code A numeric code for the sub category
 * @prop {string} subcategory_name A string name for the sub category
 * @prop {string} subcategory_weight A numeric relative weight (score) for this sub category
 */ 
/**
 * Each category
 * 
 * - For subcategories see {@link Vehicle_Sub_Categories} 
 * - Used by {@link Vehicle_Categories_Data}
 * @typedef {object} Vehicle_Categories
 * @prop {string} category_name A string name for this Category
 * @prop {string} category_code A numeric code for this Category
 * @prop {string} category_weight A numeric relative weight (score) for this Category
 * @prop {Vehicle_Sub_Categories[]} subcategories
 */ 
/** 
 * Array of vehicles
 * 
 * - For vehicle categories see {@link Vehicle_Categories}
 * - Used by {@link utils.reshapeVehicleCategoriesData}
 * 
 * @typedef {object} Vehicle_Categories_Data
 * @prop {string} code 
 * @prop {Vehicle_Categories} categories 
 */
/**
 * @typedef {object} Vehicles_Data
 * @prop {string} code SIPP code for vehicle
 * @prop {{code:string,name:string,weight:string}} category object
 * @prop {{code:string,name:string,weight:string}} sub_category sub-category object
 */

/**
 * @name reshapeVehicleCategoriesData
 * @desc Explode a collection of vehicles categories and categories' subcategories
 * shaped the same as elements in the `vehiclesList` vehicles array. See `services.gma.vehicles.list(data)` and 
 * `services.gma.vehicles.categoriesByCountryCode(data)` for more information
 * @param      {Vehicle_Categories_Data[]}  vehicleCatsByCountryCode  The vehicle cats by country code data collection
 * @return     {Vehicles_Data[]} an array of vehicles categories and subcategories data
 * @memberof utils
 * @example
 * // Given data in the format of Vehicle_Categories_Data:
 *   [{ 
 *     code,
 *   	 categories: {
 *   	 	 category_name,
 *   		 category_code,
 *   		 category_weight,
 *   		 subcategories: [
 *   			{
 *   				subcategory_code,
 *   				subcategory_name,
 *   				subcategory_weight
 *   			}, 
 *   			... // vehicle categories endpoint can have 1:many categories:subcategories
 *   		]
 *   	}
 *   },...]
 * // Returns an array with each subcategories (Vehicle_Sub_Categories) object pulled to
 * // the top level the way the Vehicles data  is provided from `services.gma.vehicles.list`
 *   [{
 *     code:string
 *   	 category: {
 *   	 	 code:string
 *   		 name:string
 *   		 weight:string
 *   	},
 *   	sub_category: {
 *   		 code:string
 *   		 name:string
 *   		 name_en:string
 *   		 weight:string
 *   	}
 *   }, ... // one entry per input subcategory
 *   ]
 */
export function reshapeVehicleCategoriesData(vehicleCatsByCountryCode) {
	return vehicleCatsByCountryCode
		?.filter((i) => i?.categories?.category_name)
		.map((val) => {
			return val.categories?.subcategories
				.map((subval) => ({
					code: val.code,
					category: {
						code: val.categories.category_code,
						name: val.categories.category_name,
						weight: val.categories.category_weight,
					},
					sub_category: {
						code: subval.subcategory_code,
						name: subval.subcategory_name,
						weight: subval.subcategory_weight,
						name_en: subval.name_en
					},
				}))
				.flat();
		})
		.flat();
}

/**
 * @name mergeVehicleData
 * @desc Take arrays of `Vehicle_Categories_Data` and `Vehicles_Data` and combine into a single array.
 * - This effectively takes the output of `reshapeVehicleCategoriesData` and combines it with `vehicles`
 * - Note we currently prioritize the collection.
 * @param      {Vehicles_Data[]}  vehicleCatDataShaped  The vehicle categories data shaped like Vehicle Data
 * @param      {Vehicles_Data[]}  vehicles              The vehicles
 * @returns  {Vehicles_Data[]} a combined array of Vehicles_Data items
 * @memberof utils
 * @see {@link utils.reshapeVehicleCategoriesData}
 * @example 
 * // given (Vehicle_Categories_Data shaped like) Vehicles_Data + Vehicles_Data, we can merge the list of vehicle items in each, based
 * // off the properties from the vehicles items, but injecting the categories data derived from
 * // `services.gma.vehicles.categoriesByCountryCode(data)` having been passed through `reshapeVehicleCategoriesData`.
 * // Effectively this replaces the categories and sub-categories data in the `vehicles` items with the
 * // category and sub-category data from the Vehicle_Categories_Data[].
 * 
 * // vehicles categories data
 * const vehicleCats = [{
 * 	code,
 * 	category: cat1,
 * 	sub_category: subcat1
 * },...]
 * // master list of vehicles data
 * const vehicles = [{
 * 	code,
 * 	category: cat2,         // this will be replaced
 * 	sub_category: subcat2,  // this will be replaced
 * 	sorted_id,
 * 	...other fields
 * },...]
 * const merged = mergeVehicleData(vehicleCats, vehicles);
 * console.log(merged);
 * // this will yeild
 * [{
 * 	code,
 * 	category: cat1,        // replaced
 * 	sub_category: subcat1, // replaced
 * 	sorted_id,
 * 	...other fields
 * },...]
 */
export function mergeVehicleData(vehicleCatDataShaped, vehicles) {
	const vcats = vehicleCatDataShaped;
	const veh = vehicles;
	const result = vcats
		.map(i => {
		// amongst the vehicles list, find a record with the same SIPP code
		const rel = veh.find(ii => ii.code === i.code);
		if (rel && rel.code) {
			return {
				...rel,
				...i 
				// overwrite the category and subcategory data with the 
				// data from the Vehicle_Categories_Data
			};
		} else {
			return null;
		}
	})
	// remove null entries
	.filter(i => i !== null);
	return result;
}
